import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "@helpers/axios/private.axios";
import publicAxios from "@helpers/axios/public.axios";
import { useSelector, useDispatch } from "react-redux";
import { PositionItem } from "./PositionItem";
import { getWallets } from "@actions/wallet";
import { clearPositionCloseRequest } from "@actions/futures";
import { showPartialCloseNotification } from "@actions/futures/positionNotifications";
import { useTranslation } from "@helpers/translate";
import {
  normalizeOrderSymbol,
  resolvePositionMarkPrice,
  symbolsMatch,
} from "@helpers/formatOrders";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ALERT, FUTURES } from "../../../../../constants";
import { getTodayStatistics } from "@actions";
import { CustomScrollTable } from "../CustomScrollTable";
import { OrderCardItem } from "./OrderCardItem";
import { useOrdersLayout } from "./OrdersLayoutContext";
import { OrdersEmptyState } from "./OrdersEmptyState";
import {
  CONFIRM_SKIP_FLAG,
  CashOutModal,
  PartialCloseModal,
  TradeDetailsModal,
  fromPosition,
  useTradeConfirmSkips,
  isDemoWallet,
} from "../TradeModals";
import {
  BINANCE_STREAM_HOST,
  isBinanceStreamLive,
  subscribeBinanceStream,
} from "@helpers/binance/binanceStreams";
import "./index.sass";
import "../../Futures.sass";
import "./Positions.sass";

const MARK_PRICE_FALLBACK_POLL_MS = 3000;

export const Positions = ({
  showAllMarkets,
  data,
  setData,
  manualCloseRef,
  onPositionHistoryInvalidate,
  onPairClick,
}) => {
  const { __ } = useTranslation();
  // const [optionsOpen, setOptionsOpen] = useState(false);
  // const checkboxId = useId();
  const {
    price,
    markPrice: activePairMarkPrice,
    tickerData,
    tickersMap,
  } = useSelector((state) => state.futures);
  const [loading, setLoading] = useState(false);
  const [markPrices, setMarkPrices] = useState({});
  // Open trade waiting for the "Cash out this trade?" confirmation
  const [cashOutOrder, setCashOutOrder] = useState(null);
  // Id, so the "Close Trade" modal follows live updates and closes with the trade
  const [partialCloseOrderId, setPartialCloseOrderId] = useState(null);
  // Id as well: the details follow live updates and close with the trade
  const [detailsOrderId, setDetailsOrderId] = useState(null);
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const { isCompactCards } = useOrdersLayout();
  const isMobile = isMobileQuery || isCompactCards;
  const { pair, positionCloseRequest, openedPosition } = useSelector(
    (state) => state.futures,
  );
  const {
    wallet: { active_wallet },
  } = useSelector((state) => state);
  const activeWalletId = active_wallet?.id;
  const dispatch = useDispatch();
  const { isConfirmSkipped } = useTradeConfirmSkips();
  const isFetchingPositionsRef = useRef(false);
  const isFetchingPricesRef = useRef(false);
  const getOrderSymbol = useCallback(
    (order) =>
      order?.symbol ?? order?.pair ?? order?.pair_name ?? order?.slug ?? "",
    [],
  );
  const isFinitePositive = useCallback(
    (value) => Number.isFinite(Number(value)) && Number(value) > 0,
    [],
  );
  const isRenderablePositionRow = useCallback(
    (item) => {
      if (!item?.id) return false;
      if (
        !item?.wallet_id ||
        !activeWalletId ||
        item.wallet_id !== activeWalletId
      ) {
        return false;
      }
      const status = String(item?.status || "").toLowerCase();
      const isOpenStatus = !["closed", "cancelled", "canceled"].includes(
        status,
      );
      if (!isOpenStatus) return false;
      return (
        isFinitePositive(item?.quantity) ||
        isFinitePositive(String(item?.size ?? "0").replace(/,/g, "")) ||
        isFinitePositive(item?.margin)
      );
    },
    [activeWalletId, isFinitePositive],
  );
  const resolveMarkPrice = useCallback(
    (order) =>
      resolvePositionMarkPrice(order, {
        markPrices,
        pairSlug: pair?.slug,
        activePairMarkPrice,
        lastPrice: price ?? tickerData?.lastPrice,
        tickersMap,
      }),
    [
      activePairMarkPrice,
      markPrices,
      pair?.slug,
      price,
      tickerData?.lastPrice,
      tickersMap,
    ],
  );

  const updateBalance = () => {
    dispatch(getWallets());
    dispatch(getTodayStatistics());
  };

  const getPositions = useCallback(
    async (silent = false) => {
      if (isFetchingPositionsRef.current) return;
      isFetchingPositionsRef.current = true;
      try {
        if (!silent) setLoading(true);
        const resp = await axios.get(`/futures/positions/open`);
        const nextData = (resp.data.data || []).filter(isRenderablePositionRow);
        setData(nextData);
      } catch (e) {
        setData([]);
      } finally {
        if (!silent) setLoading(false);
        isFetchingPositionsRef.current = false;
      }
    },
    [setData, isRenderablePositionRow],
  );

  useEffect(() => {
    const activePairSymbol = normalizeOrderSymbol(pair?.slug);
    const liveMark = Number(activePairMarkPrice);
    if (!activePairSymbol || !Number.isFinite(liveMark) || liveMark <= 0)
      return;

    setMarkPrices((prev) => {
      if (prev[activePairSymbol] === liveMark) return prev;
      return { ...prev, [activePairSymbol]: liveMark };
    });
  }, [activePairMarkPrice, pair?.slug]);

  const partialClosePosition = async (position, percent) => {
    try {
      const resp = await axios.post(
        `/futures/positions/partial-close/${position.id}`,
        { percent },
      );

      setData((currentData) =>
        currentData.map((item) =>
          item.id === position.id ? { ...item, ...resp.data.data } : item,
        ),
      );

      onPositionHistoryInvalidate?.();
      dispatch(showPartialCloseNotification({ position, percent }));
      updateBalance();
    } catch (e) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: e.response?.data?.message || "Error closing order",
      });
    }
  };

  const closePosition = async (positionId) => {
    try {
      if (manualCloseRef && manualCloseRef.current) {
        manualCloseRef.current.add(positionId);
      }

      const resp = await axios.post(`/futures/positions/close/${positionId}`, {
        price,
      });

      setData((currentData) =>
        currentData.filter((item) => item.id !== positionId),
      );

      onPositionHistoryInvalidate?.();

      dispatch({
        type: FUTURES.SHOW_NOTIFICATION,
        payload: {
          ...resp.data.data[0],
          pnl: resp.data.data?.[0]?.pnl,
          notificationType: "positionClosed",
        },
      });

      updateBalance();
    } catch (e) {
      console.error("Error closing position:", e);

      if (manualCloseRef && manualCloseRef.current) {
        manualCloseRef.current.delete(positionId);
      }

      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: e.response?.data?.message || "Error closing order",
      });
    }
  };

  const wsSymbols = useMemo(() => {
    const dataSymbols = data
      .map((item) => normalizeOrderSymbol(getOrderSymbol(item)))
      .filter(Boolean);
    const activePairSymbol = normalizeOrderSymbol(pair?.slug);
    const symbols = showAllMarkets
      ? [...new Set(dataSymbols)]
      : activePairSymbol
      ? [activePairSymbol]
      : [];
    return symbols.sort();
  }, [data, getOrderSymbol, pair?.slug, showAllMarkets]);

  const wsSymbolsKey = useMemo(() => wsSymbols.join(","), [wsSymbols]);

  useEffect(() => {
    if (!wsSymbolsKey) return;

    const unsubscribers = wsSymbolsKey.split(",").map((symbol) =>
      subscribeBinanceStream(
        BINANCE_STREAM_HOST.MARKET,
        `${symbol.toLowerCase()}@markPrice@1s`,
        (payload) => {
          const markPrice = parseFloat(payload?.p);
          if (Number.isFinite(markPrice)) {
            setMarkPrices((prev) => ({ ...prev, [symbol]: markPrice }));
          }
        },
      ),
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [wsSymbolsKey]);

  useEffect(() => {
    if (!wsSymbolsKey) return;

    const refreshPrices = async () => {
      if (isFetchingPricesRef.current) return;
      isFetchingPricesRef.current = true;
      try {
        const resp = await publicAxios.get(
          "https://fapi.binance.com/fapi/v1/ticker/price",
        );
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        const liveMap = {};
        const wanted = new Set(wsSymbolsKey.split(","));

        rows.forEach((row) => {
          const symbol = normalizeOrderSymbol(row?.symbol);
          if (!wanted.has(symbol)) return;
          const p = Number(row?.price);
          if (Number.isFinite(p) && p > 0) liveMap[symbol] = p;
        });

        if (Object.keys(liveMap).length) {
          setMarkPrices((prev) => ({ ...prev, ...liveMap }));
        }
      } catch (e) {
        // Ignore fallback polling errors; WS/global feeds still apply.
      } finally {
        isFetchingPricesRef.current = false;
      }
    };

    refreshPrices();
    // Mark prices arrive with the stream; poll only while it is down
    const intervalId = setInterval(() => {
      if (!isBinanceStreamLive(BINANCE_STREAM_HOST.MARKET)) refreshPrices();
    }, MARK_PRICE_FALLBACK_POLL_MS);
    return () => clearInterval(intervalId);
    // Keyed by the symbol set: the positions list refreshes every 3s with a new array
  }, [wsSymbolsKey]);

  useEffect(() => {
    setData([]);
  }, [activeWalletId]);

  useEffect(() => {
    getPositions();
  }, [activeWalletId, getPositions]);

  // A position opened from the form shows up at once; the polls keep it in sync afterwards
  useEffect(() => {
    const position = openedPosition?.position;

    if (!position || !isRenderablePositionRow(position)) return;

    setData((currentData) =>
      currentData.some((item) => item.id === position.id)
        ? currentData
        : [position, ...currentData],
    );
  }, [openedPosition, isRenderablePositionRow, setData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      getPositions(true);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [activeWalletId, getPositions]);

  const renderableOpenPositions = useMemo(() => {
    const positions = Array.isArray(data) ? data : [];

    return positions.filter(isRenderablePositionRow);
  }, [data, isRenderablePositionRow]);

  useEffect(() => {
    dispatch({
      type: FUTURES.SET_OPEN_POSITIONS,
      payload: renderableOpenPositions,
    });
  }, [dispatch, renderableOpenPositions]);

  useEffect(() => {
    dispatch({
      type: FUTURES.SET_POSITION_MARK_PRICES,
      payload: markPrices,
    });
  }, [dispatch, markPrices]);

  useEffect(
    () => () => {
      dispatch({
        type: FUTURES.SET_OPEN_POSITIONS,
        payload: [],
      });
      dispatch({
        type: FUTURES.SET_POSITION_MARK_PRICES,
        payload: {},
      });
    },
    [dispatch],
  );

  const filteredData = useMemo(() => {
    const walletSafeData = data.filter(isRenderablePositionRow);
    if (!showAllMarkets && pair?.slug) {
      return walletSafeData.filter((order) =>
        symbolsMatch(getOrderSymbol(order), pair.slug),
      );
    }
    return walletSafeData;
  }, [data, getOrderSymbol, isRenderablePositionRow, pair, showAllMarkets]);
  /*
  const mobileCheckboxRow = (
    <div className="position-item__checkbox-wrap">
      <input
        className="position-item__checkbox-input"
        id={checkboxId}
        checked={optionsOpen}
        onChange={() => setOptionsOpen((prev) => !prev)}
        type="checkbox"
      />
      <label htmlFor={checkboxId} className="position-item__checkbox-label">
        <p>{__("futures.tp_sl.short_label")}</p>
      </label>
    </div>
  );
  */

  const partialCloseOrder =
    filteredData.find((order) => order.id === partialCloseOrderId) ?? null;

  const openPartialClose = (order) => setPartialCloseOrderId(order.id);

  const detailsOrder =
    filteredData.find((order) => order.id === detailsOrderId) ?? null;

  const openDetails = (order) => setDetailsOrderId(order.id);

  // Closes at once when "Don't ask again" was ticked before
  const requestCashOut = (order) =>
    isConfirmSkipped(CONFIRM_SKIP_FLAG.CASH_OUT)
      ? closePosition(order.id)
      : setCashOutOrder(order);

  // Close asked from the chart goes through the same confirmation as the table
  useEffect(() => {
    if (!positionCloseRequest) return;

    dispatch(clearPositionCloseRequest());

    const order = data.find(
      (item) => item.id === positionCloseRequest.positionId,
    );

    if (order) requestCashOut(order);
    // requestCashOut is recreated each render, the request is the trigger
  }, [positionCloseRequest]);

  const tradeModals = (
    <>
      <CashOutModal
        isOpen={Boolean(cashOutOrder)}
        onClose={() => setCashOutOrder(null)}
        onConfirm={async () => {
          await closePosition(cashOutOrder.id);
          setCashOutOrder(null);
        }}
      />

      <PartialCloseModal
        order={partialCloseOrder}
        markPrice={partialCloseOrder ? resolveMarkPrice(partialCloseOrder) : null}
        onClose={() => setPartialCloseOrderId(null)}
        onCloseTrade={async (order) => {
          await closePosition(order.id);
          setPartialCloseOrderId(null);
        }}
        onPartialClose={async (order, percent) => {
          await partialClosePosition(order, percent);
          setPartialCloseOrderId(null);
        }}
      />

      <TradeDetailsModal
        trade={
          detailsOrder &&
          fromPosition(detailsOrder, {
            placedBy: __("futures.trade_details.me"),
            isDemo: isDemoWallet(active_wallet),
          })
        }
        livePrice={detailsOrder ? resolveMarkPrice(detailsOrder) : null}
        onClose={() => setDetailsOrderId(null)}
      />
    </>
  );

  if (isMobile) {
    const mobileList = loading ? (
      <div className="position-item-table__mobile">
        <div className="order-card-list">
          {[1, 2, 3, 4, 5].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              height={72}
              sx={{ width: "100%", borderRadius: "12px", bgcolor: "#101215" }}
            />
          ))}
        </div>
      </div>
    ) : filteredData.length > 0 ? (
      <div className="position-item-table__mobile">
        <CustomScrollTable
          isTable={false}
          maxHeight={392}
          className="order-card-list"
        >
          {filteredData.map((order, index) => (
            <OrderCardItem
              key={order.id ?? index}
              order={order}
              markPrice={resolveMarkPrice(order)}
              isPosition={true}
              onPairClick={onPairClick}
              onCardClick={() => openPartialClose(order)}
            />
          ))}
        </CustomScrollTable>
      </div>
    ) : (
      <OrdersEmptyState className="orders-empty-state--fill" />
    );

    return (
      <>
        {mobileList}
        {tradeModals}
      </>
    );
  }

  return (
    <div className="trade-table">
      <CustomScrollTable
        className="futures-orders-table futures-positions-table futures-open-trades"
        scrollable={loading || filteredData.length > 0}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow className="futures-orders-table__head-row-main">
              <TableCell className="trade-table__head trade-table__head--pair">
                {__("futures.community.trade")}
              </TableCell>
              <TableCell className="trade-table__head">
                {__("futures.community.wager")}
              </TableCell>
              <TableCell className="trade-table__head">
                {__("futures.community.leverage")}
              </TableCell>
              <TableCell className="trade-table__head">
                {__("futures.community.entry")}
              </TableCell>
              <TableCell className="trade-table__head">
                {__("futures.liquidation")}
              </TableCell>
              <TableCell className="trade-table__head">
                {__("futures.community.profit_loss")}
              </TableCell>
              <TableCell
                className="trade-table__head"
                data-futures-guide="manage-position-tp-sl"
              >
                {__("futures.tp_sl.column")}
              </TableCell>
              <TableCell
                className="trade-table__head trade-table__head--actions"
                data-futures-guide="manage-position-close"
              >
                {__("futures.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={8}>
                    <Skeleton
                      className="main-skeleton"
                      variant="rounded"
                      height={20}
                      width="100%"
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredData.length > 0 ? (
              filteredData.map((order) => (
                <PositionItem
                  key={order.id}
                  order={order}
                  onCloseClick={requestCashOut}
                  onPartialCloseClick={openPartialClose}
                  onRowClick={openDetails}
                  markPrice={resolveMarkPrice(order)}
                  onPairClick={onPairClick}
                />
              ))
            ) : (
              <TableRow className="trade-table__empty-row">
                <TableCell className="trade-table__empty" colSpan={8}>
                  <OrdersEmptyState className="orders-empty-state--in-table" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CustomScrollTable>

      {tradeModals}
    </div>
  );
};
